import React, { useState } from 'react';
import { Table, Card, Tag, Button, Modal, Space, Typography, Tooltip } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../../api/axios';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface ContactMessageDto {
    id: string;
    firstName: string;
    lastName?: string;
    email: string;
    phone?: string;
    subject: string;
    message: string;
    isRead: boolean;
    createdAt: string;
}

const ContactMessagesList: React.FC = () => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [viewMessage, setViewMessage] = useState<ContactMessageDto | null>(null);
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['adminContactMessages', currentPage, pageSize],
        queryFn: async () => {
            const response = await apiClient.get('/admin/contact/messages', {
                params: { pageNumber: currentPage, pageSize: pageSize }
            });
            return response.data;
        }
    });

    const updateStatusMutation = useMutation({
        mutationFn: async ({ id, isRead }: { id: string, isRead: boolean }) => {
            await apiClient.patch(`/admin/contact/messages/${id}/status`, isRead, {
                headers: { 'Content-Type': 'application/json' }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminContactMessages'] });
        }
    });

    const handleViewMessage = (record: ContactMessageDto) => {
        setViewMessage(record);
        if (!record.isRead) {
            updateStatusMutation.mutate({ id: record.id, isRead: true });
        }
    };

    const handleToggleRead = () => {
        if (viewMessage) {
            const newStatus = !viewMessage.isRead;
            updateStatusMutation.mutate({ id: viewMessage.id, isRead: newStatus });
            setViewMessage({ ...viewMessage, isRead: newStatus });
        }
    };

    const columns = [
        {
            title: t('date', 'Datum'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text: string) => dayjs(text).format('DD.MM.YYYY HH:mm'),
            width: 150
        },
        {
            title: t('sender', 'Absender'),
            key: 'sender',
            render: (_: any, record: ContactMessageDto) => (
                <Space direction="vertical" size={0}>
                    <Text strong style={{ opacity: record.isRead ? 0.6 : 1 }}>{record.firstName} {record.lastName}</Text>
                    <Text type="secondary" className="text-xs">{record.email}</Text>
                    {record.phone && <Text type="secondary" className="text-xs">{record.phone}</Text>}
                </Space>
            )
        },
        {
            title: t('subject', 'Betreff'),
            dataIndex: 'subject',
            key: 'subject',
            ellipsis: true,
            render: (text: string, record: ContactMessageDto) => (
                <Text strong={!record.isRead} style={{ opacity: record.isRead ? 0.7 : 1 }}>{text}</Text>
            )
        },
        {
            title: t('status', 'Status'),
            dataIndex: 'isRead',
            key: 'isRead',
            render: (isRead: boolean) => (
                <Tag color={isRead ? 'default' : 'gold'} style={{ borderRadius: '10px' }}>
                    {isRead ? t('read', 'Gelesen') : t('new', 'Neu')}
                </Tag>
            ),
            width: 100
        },
        {
            title: t('actions', 'Aktionen'),
            key: 'actions',
            render: (_: any, record: ContactMessageDto) => (
                <Tooltip title={t('view_message', 'Nachricht anzeigen')}>
                    <Button 
                        type="text" 
                        icon={<EyeOutlined />} 
                        onClick={() => handleViewMessage(record)} 
                    />
                </Tooltip>
            ),
            width: 100
        }
    ];

    return (
        <Card title={t('contact_messages', 'Kontaktanfragen')}>
            <Table
                columns={columns}
                dataSource={data?.data || []}
                rowKey="id"
                loading={isLoading}
                rowClassName={(record) => record.isRead ? 'table-row-read opacity-80' : 'table-row-unread font-bold'}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: data?.totalRecords || 0,
                    onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    },
                    showSizeChanger: true
                }}
                onRow={(record) => ({
                    onClick: () => handleViewMessage(record),
                    className: 'cursor-pointer'
                })}
            />

            <Modal
                title={t('message_details', 'Nachricht Details')}
                open={!!viewMessage}
                onCancel={() => setViewMessage(null)}
                footer={[
                    <Button 
                        key="toggle" 
                        onClick={handleToggleRead}
                        loading={updateStatusMutation.isPending}
                    >
                        {viewMessage?.isRead ? t('mark_as_unread', 'Als ungelesen markieren') : t('mark_as_read', 'Als gelesen markieren')}
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setViewMessage(null)}>
                        {t('close', 'Schließen')}
                    </Button>
                ]}
                width={700}
            >
                {viewMessage && (
                    <div className="space-y-4 pt-4">
                        <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded text-sm">
                            <div><Text type="secondary">{t('date', 'Datum')}:</Text><br /><Text strong>{dayjs(viewMessage.createdAt).format('DD.MM.YYYY HH:mm')}</Text></div>
                            <div><Text type="secondary">{t('status', 'Status')}:</Text><br /><Tag color={viewMessage.isRead ? 'default' : 'gold'}>{viewMessage.isRead ? t('read', 'Gelesen') : t('unread', 'Neu')}</Tag></div>
                            <div><Text type="secondary">{t('name', 'Name')}:</Text><br /><Text strong>{viewMessage.firstName} {viewMessage.lastName}</Text></div>
                            <div><Text type="secondary">{t('email', 'E-Mail')}:</Text><br /><Text strong>{viewMessage.email}</Text></div>
                            {viewMessage.phone && <div><Text type="secondary">{t('phone', 'Telefon')}:</Text><br /><Text strong>{viewMessage.phone}</Text></div>}
                        </div>
                        
                        <div>
                            <Text type="secondary">{t('subject', 'Betreff')}:</Text>
                            <div className="text-lg font-medium mt-1">{viewMessage.subject}</div>
                        </div>
                        
                        <div>
                            <Text type="secondary">{t('message', 'Nachricht')}:</Text>
                            <div className="mt-2 p-4 border rounded bg-white whitespace-pre-wrap font-sans text-base shadow-inner">
                                {viewMessage.message}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </Card>
    );
};

export default ContactMessagesList;
